import React, { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mantine/core";

import { PageProps } from "@sdflc/react-router-pages";

import { Client } from "@/utils/api";
import { useClients } from "@/hooks";

import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
} from "@/components/generic";
import { ClientFormModal, ClientsTable } from "@/components/features";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const ClientsPage: React.FC<PageProps> = (props: PageProps) => {
  const navigate = useNavigate();

  // Use the clients hook for all data management (without autoLoad)
  const {
    // Data state
    client,
    clients,
    loading,
    loadingMore,
    hasMore,
    isEmpty,

    // Search and filters
    filters,
    sortBy,

    // Operations state
    isCreating,
    isUpdating,
    removingClientId,

    // Data operations
    list,
    loadMore,
    refresh,

    // Search and filter operations
    setSortBy,

    // CRUD operations
    create,
    update,
    remove,

    // Error handling
    lastError,
    clearError,
  } = useClients({
    autoLoad: false, // Disable autoLoad to control it manually
  });

  // Local UI state
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Local search state for debouncing
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitialLoadRef = useRef<boolean>(false);

  // Initial load effect - runs only once on mount
  useEffect(() => {
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
      // Load initial data with current filters
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    }
  }, []); // Empty dependency array - runs only once

  // Handle search input changes with debouncing
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Set new timeout for search (only if we've done initial load)
      if (hasInitialLoadRef.current) {
        searchTimeoutRef.current = setTimeout(() => {
          const updatedFilters = {
            ...filters,
            searchKeyword: value || undefined,
          };

          list(updatedFilters, { page: 1, pageSize: 20 }, sortBy, true);
        }, 1000); // 1 second delay
      }
    },
    [filters, sortBy]
  ); // Stable dependencies

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Handle add client button click
  const handleAddClient = useCallback(() => {
    setSelectedClient({ id: "" });
  }, []);

  // Handle edit client
  const handleEditClient = useCallback((client: Client) => {
    setSelectedClient(client);
  }, []);

  // Handle view client
  const handleViewClient = useCallback(
    (client: Client) => {
      navigate(`/clients/${client.id}/details`);
    },
    [navigate]
  );

  // Handle remove client button click
  const handleRemoveClientClick = useCallback((client: Client) => {
    setClientToDelete(client);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (clientToDelete) {
      const success = await remove({ id: clientToDelete.id });
      if (success) {
        setClientToDelete(null);
      }
      // Error handling is managed by the hook
    }
  }, [clientToDelete, remove]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setClientToDelete(null);
  }, []);

  // Handle form modal close
  const handleFormModalClose = useCallback(() => {
    setSelectedClient(null);
    clearError();
  }, [clearError]);

  // Handle create client
  const handleCreateClient = useCallback(
    async (clientData: any) => {
      const result = await create(clientData);
      return result;
    },
    [create]
  );

  // Handle update client
  const handleUpdateClient = useCallback(
    async (client: Client, clientData: any) => {
      const result = await update({ id: client.id }, clientData);
      return result;
    },
    [update]
  );

  // Handle refresh
  const handleRefresh = useCallback(() => {
    // Clear search timeout and refresh with current filters
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    refresh();
  }, [refresh]);

  // Handle export (placeholder)
  // const handleExport = useCallback(() => {
  //   // TODO: Implement export functionality
  //   console.log("Export clients");
  // }, []);

  // Format client name for delete confirmation - handles null/undefined
  const getClientDisplayName = (client: Client | null | undefined): string => {
    if (!client) {
      return "Unknown Client";
    }

    if (client.companyName) {
      return client.companyName;
    }

    const nameParts = [];
    if (client.nameTitle) nameParts.push(client.nameTitle);
    if (client.firstName) nameParts.push(client.firstName);
    if (client.lastName) nameParts.push(client.lastName);

    return nameParts.length > 0 ? nameParts.join(" ") : "Unnamed Client";
  };

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Page toolbar with actions and search */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "actions",
              items: [
                <AppButton
                  key="add-client"
                  iconName="Plus"
                  onClick={handleAddClient}
                  disabled={isCreating || !!removingClientId || loading}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={isCreating || !!removingClientId || loading}
                >
                  Refresh
                </AppButton>,
                /*<AppButton
                  key="export"
                  variant="secondary"
                  iconName="Download"
                  onClick={handleExport}
                  disabled={isEmpty}
                >
                  Export
                </AppButton>,*/
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "search",
              items: [
                <SearchInput
                  key="search"
                  placeholder="Search clients..."
                  value={searchKeyword}
                  onChange={(value) => handleSearchChange(value)}
                  style={{ minWidth: 250 }}
                />,
              ],
              align: "right",
            },
          ]}
        />

        {/* Clients table */}
        <ClientsTable
          clients={clients}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={hasMore}
          sortBy={sortBy}
          onSortChange={setSortBy}
          onLoadMore={loadMore}
          onEditClient={handleEditClient}
          onRemoveClient={handleRemoveClientClick}
          onViewClient={handleViewClient}
          removingClientId={removingClientId}
          editedItemId={client?.id}
        />

        {/* Empty state when no clients */}
        <EmptyState
          display={isEmpty && !loading}
          title="No clients yet"
          description="Start by adding your first client to manage your customer relationships"
          actionIcon="Plus"
          actionLabel="Add Client"
          onAction={handleAddClient}
        />
      </Stack>

      {/* Client form modal for create/edit */}
      <ClientFormModal
        client={selectedClient}
        opened={!!selectedClient}
        onClose={handleFormModalClose}
        onCreateClient={handleCreateClient}
        onUpdateClient={handleUpdateClient}
        isCreating={isCreating}
        isUpdating={isUpdating}
        lastError={lastError}
        onClearError={clearError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={!!clientToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Client"
        message={`Are you sure you want to delete "${getClientDisplayName(
          clientToDelete
        )}"?`}
        details="This action cannot be undone. The client record and all associated data will be permanently removed."
        confirmInProgress={removingClientId === clientToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
